import Dexie, { type Table } from "dexie";

export interface PublicCatalogRecord {
  key: string;
  payload: unknown;
  updatedAt: number;
}

export interface PrivateSnapshotRecord {
  ownerId: string;
  key: string;
  payload: unknown;
  updatedAt: number;
}

export interface LocalSessionRecord {
  key: "current-owner";
  ownerId: string;
  updatedAt: number;
}

export interface CatalogEpisodeRecord {
  publicId: string;
  showPublicId: string;
  installmentPublicId: string;
  payload: unknown;
  updatedAt: number;
  accessedAt: number;
}

export interface TrackingShowRecord {
  ownerId: string;
  showPublicId: string;
  payload: unknown;
  updatedAt: number;
}

export interface TrackingEpisodeRecord {
  ownerId: string;
  episodePublicId: string;
  showPublicId: string;
  installmentPublicId: string | null;
  payload: unknown;
  updatedAt: number;
}

export interface ClientNoticeRecord {
  id: string;
  ownerId: string;
  kind: "offline-writes-retired-v4";
  discardedMutationCount: number;
  createdAt: number;
}

interface LegacyMutationOutboxRecord {
  operationId: string;
  ownerId: string;
  payload: unknown;
  status: "pending" | "failed" | "conflict";
  updatedAt: number;
  attempts?: number;
  idempotencyKey?: string;
}

const OFFLINE_WRITES_RETIRED_NOTICE = "offline-writes-retired-v4" as const;

export function offlineWritesRetiredNoticeId(ownerId: string) {
  return `${OFFLINE_WRITES_RETIRED_NOTICE}:${ownerId}`;
}

export class OfflineDatabase extends Dexie {
  publicCatalog!: Table<PublicCatalogRecord, string>;
  privateSnapshots!: Table<PrivateSnapshotRecord, [string, string]>;
  localSession!: Table<LocalSessionRecord, string>;
  catalogEpisodes!: Table<CatalogEpisodeRecord, string>;
  trackingShows!: Table<TrackingShowRecord, [string, string]>;
  trackingEpisodes!: Table<TrackingEpisodeRecord, [string, string]>;
  clientNotices!: Table<ClientNoticeRecord, string>;

  constructor(name = "track-my-tv-show") {
    super(name);
    this.version(1).stores({
      publicCatalog: "key, updatedAt",
      privateSnapshots: "[ownerId+key], ownerId, updatedAt",
      mutationOutbox: "operationId, ownerId, status, updatedAt",
      syncMetadata: "[ownerId+key], ownerId, updatedAt",
    });
    this.version(2)
      .stores({
        publicCatalog: "key, updatedAt",
        privateSnapshots: "[ownerId+key], ownerId, updatedAt",
        mutationOutbox:
          "operationId, idempotencyKey, ownerId, status, [ownerId+status], [ownerId+idempotencyKey], updatedAt",
        syncMetadata: "[ownerId+key], ownerId, updatedAt",
        localSession: "&key, ownerId, updatedAt",
      })
      .upgrade(async (transaction) => {
        await transaction
          .table<LegacyMutationOutboxRecord, string>("mutationOutbox")
          .toCollection()
          .modify((record) => {
            record.idempotencyKey ||= record.operationId;
            record.attempts ??= 0;
          });
      });
    this.version(3)
      .stores({
        publicCatalog: "key, updatedAt",
        privateSnapshots: "[ownerId+key], ownerId, updatedAt",
        mutationOutbox:
          "operationId, idempotencyKey, ownerId, status, [ownerId+status], [ownerId+idempotencyKey], updatedAt",
        syncMetadata: "[ownerId+key], ownerId, updatedAt",
        localSession: "&key, ownerId, updatedAt",
        catalogEpisodes:
          "&publicId, showPublicId, installmentPublicId, [installmentPublicId+updatedAt], accessedAt",
        trackingShows: "[ownerId+showPublicId], ownerId, updatedAt",
        trackingEpisodes:
          "[ownerId+episodePublicId], [ownerId+showPublicId], [ownerId+installmentPublicId], ownerId, updatedAt",
      })
      .upgrade(async (transaction) => {
        const publicCatalog = transaction.table<PublicCatalogRecord, string>("publicCatalog");
        const catalogEpisodes = transaction.table<CatalogEpisodeRecord, string>("catalogEpisodes");
        const privateSnapshots = transaction.table<PrivateSnapshotRecord, [string, string]>(
          "privateSnapshots",
        );
        const trackingShows = transaction.table<TrackingShowRecord, [string, string]>(
          "trackingShows",
        );
        const trackingEpisodes = transaction.table<TrackingEpisodeRecord, [string, string]>(
          "trackingEpisodes",
        );

        await publicCatalog
          .where("key")
          .startsWith("catalog:detail:")
          .each(async (record) => {
            const show = record.payload as {
              publicId?: unknown;
              installments?: unknown;
            };
            if (typeof show.publicId !== "string" || !Array.isArray(show.installments)) return;
            const episodeRecords: CatalogEpisodeRecord[] = [];
            for (const value of show.installments) {
              const installment = value as { publicId?: unknown; episodes?: unknown };
              if (
                typeof installment.publicId !== "string" ||
                !Array.isArray(installment.episodes)
              ) {
                continue;
              }
              for (const episodeValue of installment.episodes) {
                const episode = episodeValue as { publicId?: unknown };
                if (typeof episode.publicId !== "string") continue;
                episodeRecords.push({
                  publicId: episode.publicId,
                  showPublicId: show.publicId,
                  installmentPublicId: installment.publicId,
                  payload: episodeValue,
                  updatedAt: record.updatedAt,
                  accessedAt: record.updatedAt,
                });
              }
            }
            if (episodeRecords.length > 0) await catalogEpisodes.bulkPut(episodeRecords);
          });

        await privateSnapshots.toCollection().each(async (record) => {
          if (!record.key.startsWith("tracking:show:")) return;
          const snapshot = record.payload as {
            showPublicId?: unknown;
            episodes?: unknown;
          };
          if (typeof snapshot.showPublicId !== "string" || !Array.isArray(snapshot.episodes)) {
            return;
          }
          const showPublicId = snapshot.showPublicId;
          const episodeValues: unknown[] = snapshot.episodes;
          const summary = { ...snapshot };
          delete summary.episodes;
          await trackingShows.put({
            ownerId: record.ownerId,
            showPublicId,
            payload: { ...summary, projectionStatus: "ready" },
            updatedAt: record.updatedAt,
          });
          await trackingEpisodes.bulkPut(
            episodeValues.flatMap((episodeValue) => {
              const episode = episodeValue as {
                episodePublicId?: unknown;
                installmentPublicId?: unknown;
              };
              if (typeof episode.episodePublicId !== "string") return [];
              return [
                {
                  ownerId: record.ownerId,
                  episodePublicId: episode.episodePublicId,
                  showPublicId,
                  installmentPublicId:
                    typeof episode.installmentPublicId === "string"
                      ? episode.installmentPublicId
                      : null,
                  payload: episodeValue,
                  updatedAt: record.updatedAt,
                },
              ];
            }),
          );
        });
      });
    this.version(4)
      .stores({
        publicCatalog: "key, updatedAt",
        privateSnapshots: "[ownerId+key], ownerId, updatedAt",
        mutationOutbox: null,
        syncMetadata: null,
        localSession: "&key, ownerId, updatedAt",
        catalogEpisodes:
          "&publicId, showPublicId, installmentPublicId, [installmentPublicId+updatedAt], accessedAt",
        trackingShows: "[ownerId+showPublicId], ownerId, updatedAt",
        trackingEpisodes:
          "[ownerId+episodePublicId], [ownerId+showPublicId], [ownerId+installmentPublicId], ownerId, updatedAt",
        clientNotices: "&id, [ownerId+kind], ownerId, kind, createdAt",
      })
      .upgrade(async (transaction) => {
        const mutationOutbox = transaction.table<LegacyMutationOutboxRecord, string>(
          "mutationOutbox",
        );
        const syncMetadata = transaction.table("syncMetadata");
        const privateSnapshots = transaction.table<PrivateSnapshotRecord, [string, string]>(
          "privateSnapshots",
        );
        const trackingShows = transaction.table<TrackingShowRecord, [string, string]>(
          "trackingShows",
        );
        const trackingEpisodes = transaction.table<TrackingEpisodeRecord, [string, string]>(
          "trackingEpisodes",
        );
        const clientNotices = transaction.table<ClientNoticeRecord, string>("clientNotices");
        const discarded = await mutationOutbox.toArray();
        const discardedByOwner = new Map<string, number>();

        for (const record of discarded) {
          discardedByOwner.set(record.ownerId, (discardedByOwner.get(record.ownerId) ?? 0) + 1);
        }
        if (discardedByOwner.size > 0) {
          const createdAt = Date.now();
          await clientNotices.bulkPut(
            [...discardedByOwner].map(([ownerId, discardedMutationCount]) => ({
              id: offlineWritesRetiredNoticeId(ownerId),
              ownerId,
              kind: OFFLINE_WRITES_RETIRED_NOTICE,
              discardedMutationCount,
              createdAt,
            })),
          );
        }

        await mutationOutbox.clear();
        await syncMetadata.clear();
        await trackingShows.clear();
        await trackingEpisodes.clear();
        await privateSnapshots
          .filter(
            (record) =>
              record.key.startsWith("tracking:show:") || record.key === "tracking:up-next",
          )
          .delete();
      });
  }
}

export const offlineDatabase = new OfflineDatabase();

export interface PrivateOfflineStore {
  clearPrivateData(): Promise<void>;
}

export function createPrivateOfflineStore(database: OfflineDatabase): PrivateOfflineStore {
  return {
    async clearPrivateData() {
      await database.transaction(
        "rw",
        [
          database.privateSnapshots,
          database.localSession,
          database.trackingShows,
          database.trackingEpisodes,
          database.clientNotices,
        ],
        async () => {
          await Promise.all([
            database.privateSnapshots.clear(),
            database.localSession.clear(),
            database.trackingShows.clear(),
            database.trackingEpisodes.clear(),
            database.clientNotices.clear(),
          ]);
        },
      );
    },
  };
}

export const privateOfflineStore = createPrivateOfflineStore(offlineDatabase);
